import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { ScopeService } from '../services/scope.service';
import { GlobalVariables } from '../constants/global-constants';
import { UtilitiesService } from '../services/utilities.service';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ProfileService } from '../services/profile.service';

export const authGuard: CanActivateFn = (route): Observable<boolean | UrlTree> => {
  const router = inject(Router);
  const cookieService = inject(CookieService);
  const scopeService = inject(ScopeService);
  const utilities = inject(UtilitiesService);
  const profileService = inject(ProfileService);

  const newRoute = route.url[0]?.path;
  const publicURL: string[] = ['forgot-password', 'login', 'change-password'];

  const tokenExists = cookieService.check('token');
  const expiresAt = localStorage.getItem('expiresAt');

  let expiresAtNum = 0;
  if (expiresAt) {
    if (!isNaN(Number(expiresAt))) {
      expiresAtNum = Number(expiresAt); // It's a timestamp (embed flow)
    } else {
      expiresAtNum = new Date(expiresAt).getTime(); // It's an ISO string (regular login)
    }
  }

  const isTokenValid =
    !!tokenExists &&
    !!expiresAt &&
    !isNaN(expiresAtNum) &&
    expiresAtNum > Date.now();

  // allow public routes if token is invalid
  if (!isTokenValid && publicURL.includes(newRoute)) {
    return of(true);
  }

  // If token is invalid and route is protected, redirect to login
  if (!isTokenValid && !publicURL.includes(newRoute)) {
    console.log('[AUTH GUARD] ❌ Token invalid, redirecting to login');
    cookieService.delete('token');
    profileService.invalidateCache();
    localStorage.removeItem('expiresAt');
    return of(router.createUrlTree(['login']));
  }

  // Token is valid — now fetch the user profile before continuing any other validation
  return profileService.getProfileCached().pipe(
    switchMap((response: any) => {
      const role = response.profile.role;
      const demoRoles = ['demo_client_admin', 'demo_client_user', 'demo_client_admin_w_onboarding_wizard'];
      const demoOnb = ['demo_client_admin_w_onboarding_wizard'];
      const fdUsers = ['system_admin', 'fd_admin', 'fd_user',];
      const acquirerUsers = ['acquirer_admin', 'acquirer_user'];

      // Save user info into the UtilitiesService
      utilities.setUserEmail(response.profile.email);
      utilities.setRole(role);
      utilities.setIsDemo(demoRoles.includes(role));
      utilities.setIsFdUser(fdUsers.includes(role));
      utilities.setIsDemoOnb(demoOnb.includes(role));
      utilities.setIsAcquirerUser(acquirerUsers.includes(role));

      const resourceRoute = GlobalVariables.ResourceURL;
      const actionsRoute = GlobalVariables.ActionsURL;

      let requiredParam = route.parent?.params?.['clientId'];
      const isUnderGlobal = route.pathFromRoot.some(r => r.routeConfig?.path === 'global');

      // save the url clientId if it's not saved yet
      if (requiredParam && !utilities.clientId) {
        utilities.clientId = requiredParam;
      } else if (utilities.clientId && !requiredParam) {
        requiredParam = utilities.clientId;
      }

      // allow parent route 'client'
      if (newRoute === 'client' && (!isNaN(route.params['clientId']) || requiredParam)) {
        return of(true);
      }

      // if route is public but user is already authenticated, redirect to select-client
      if (isTokenValid && publicURL.includes(newRoute)) {
        return of(router.createUrlTree(['select-client']));
      }

      // allow access to these general routes without scope validation
      if (
        newRoute === 'select-client' ||
        newRoute === 'queue' ||
        newRoute === 'tables' ||
        newRoute === 'admin' ||
        newRoute === 'quicksight' ||
        newRoute === 'page-not-found' ||
        newRoute === 'titan' ||
        newRoute === 'tools' ||
        newRoute === 'fdid' ||
        newRoute === 'onboarding' ||
        newRoute === 'client-onboarding' ||
        newRoute === 'mids-management' ||
        newRoute === 'create-new-merchant' ||
        newRoute === 'decision' ||
        newRoute === 'single-merchant' ||
        newRoute === 'global' ||
        newRoute === 'onboarding-in-process' ||
        newRoute === 'contract-acknowledgment'

      ) {
        return of(true);
      }

      if (isUnderGlobal) {
        return of(true);
      }

      // if clientId is missing or invalid, redirect to select-client
      if (!requiredParam || isNaN(requiredParam)) {
        return of(router.createUrlTree(['select-client']));
      }

      const actionScope = actionsRoute[newRoute];
      const resourceScope = resourceRoute[newRoute];
      const action = route?.url?.[1]?.path;

      // if scopes haven't been fetched yet, fetch and then check permissions
      if (scopeService.Scope.length === 0) {
        return scopeService.fetchScopes(utilities.clientId).pipe(
          map(scopes => {
            scopeService.Scope = scopes.scopes;

            if (
              actionScope &&
              scopeService.actionsScope(actionScope['resource'], actionScope['actions']).length > 0
            ) {
              return true;
            }

            if (
              resourceScope &&
              scopeService.resourceScope(resourceScope)?.[0]?.actions?.includes('read')
            ) {
              if (
                action === 'create' &&
                !scopeService.resourceScope(resourceScope)?.[0]?.actions?.includes(action)
              ) {
                utilities.redirectToDefaultURL();
                return false;
              }

              return true;
            }

            return router.createUrlTree(['select-client']);
          }),
          catchError(() => of(router.createUrlTree(['select-client'])))
        );
      }

      // Validate action-based scope
      if (
        actionScope &&
        scopeService.actionsScope(actionScope['resource'], actionScope['actions']).length > 0
      ) {
        return of(true);
      }

      // Validate resource-based read access
      if (
        resourceScope &&
        scopeService.resourceScope(resourceScope)?.[0]?.actions?.includes('read')
      ) {
        // If trying to create but doesn't have permission
        if (
          action === 'create' &&
          !scopeService.resourceScope(resourceScope)?.[0]?.actions?.includes(action)
        ) {
          utilities.redirectToDefaultURL();
          return of(false);
        }

        return of(true);
      }

      // If no matching access, redirect
      return of(router.createUrlTree(['select-client']));
    }),
    catchError(() => {
      router.navigate(['/login']);
      return of(false);
    })
  );
};
